/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 */

import {Component, inject, OnInit} from '@angular/core';
import {FormBuilder, FormGroup} from '@angular/forms';
import {Router} from '@angular/router';
import {
  IxActiveModal,
  ToastService,
  UploadFileState,
} from '@siemens/ix-angular';
import {DppService} from '../../../../services/dpp.service';
import {CreateShellRequest} from '../../../../models/dpp/CreateShellRequest';
import {MultiLanguageText} from '../../../../models/dpp/multi-language-text';
import {TranslateService} from '@ngx-translate/core';
import {HttpErrorResponse, HttpStatusCode} from '@angular/common/http';

@Component({
  selector: 'app-modal-create-dpp',
  templateUrl: './modal-create-dpp.component.html',
  styleUrls: ['./modal-create-dpp.component.scss'],
  standalone: false,
})
export class ModalCreateDppComponent implements OnInit {
  protected readonly activeModal: IxActiveModal = inject(IxActiveModal);
  private readonly fb: FormBuilder = inject(FormBuilder);
  private readonly dppService: DppService = inject(DppService);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly translateService: TranslateService =
    inject(TranslateService);
  private readonly router: Router = inject(Router);

  dppForm: FormGroup;
  selectedFile?: File;
  isSubmitting = false;
  uploadState = UploadFileState.SELECT_FILE;
  uploadErrorMessage = '';

  ngOnInit(): void {
    this.dppForm = this.fb.group({
      aasId: [''],
      globalAssetId: [''],
      passportName: [''],
    });
  }

  isAddButtonDisabled(): boolean {
    return this.isSubmitting;
  }

  onFilesChanged(event: CustomEvent<File[]>): void {
    this.uploadErrorMessage = '';
    this.uploadState = UploadFileState.SELECT_FILE;
    const file = event.detail.at(0);
    if (file) {
      this.selectedFile = file;
    }
  }

  async onSubmit(): Promise<void> {
    if (this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    const formValue = this.dppForm.value;

    try {
      const displayName = formValue.passportName?.trim()
        ? ([
            {language: 'en', text: formValue.passportName.trim()},
          ] as MultiLanguageText[])
        : undefined;

      const request: CreateShellRequest = {
        aasId: formValue.aasId?.trim() || undefined,
        globalAssetId: formValue.globalAssetId?.trim() || undefined,
        displayName,
      };

      const response = await this.dppService.createDpp(request);

      // Upload thumbnail if file is selected
      if (this.selectedFile && response.id) {
        try {
          this.uploadState = UploadFileState.LOADING;
          await this.dppService.uploadThumbnail(response.id, this.selectedFile);
          this.uploadState = UploadFileState.UPLOAD_SUCCESSED;
        } catch {
          this.uploadState = UploadFileState.UPLOAD_FAILED;
          this.uploadErrorMessage = this.translateService.instant(
            'dpp.modal.thumbnail_upload_warning',
          );
          this.toastService.show({
            message: this.translateService.instant(
              'dpp.modal.thumbnail_upload_warning',
            ),
            type: 'warning',
          });
        }
      }

      this.toastService.show({
        message: this.translateService.instant('dpp.modal.success'),
        type: 'success',
      });

      this.activeModal.close(response);
      await this.router.navigate(['/app/dpp/view-passport', response.id]);
    } catch (error) {
      this.handleError(error);
    } finally {
      this.isSubmitting = false;
    }
  }

  private handleError(error: unknown): void {
    let errorMessage = '';

    if (
      error instanceof HttpErrorResponse &&
      error.status === HttpStatusCode.Conflict
    ) {
      errorMessage = this.translateService.instant('dpp.modal.error_conflict');
    } else if (error instanceof Error) {
      errorMessage = error.message;
    } else {
      errorMessage = this.translateService.instant('dpp.modal.error_generic');
    }

    this.activeModal.dismiss('error');
    this.toastService.show({
      message: errorMessage,
      type: 'error',
    });
  }
  onCancel(): void {
    this.activeModal.dismiss('dismiss');
  }
}
